import { useCallback, useEffect, useState } from "react";
import axiosInstance from "../../config/axiosApi/axiosApi.jsx";
import { useDispatch } from "react-redux";
import { Link, useNavigate } from "react-router-dom";
import Preloader from "../../components/CustomComponents/preLoad/preLoader.js";
import showErrorToast, {
  showSuccessToast,
} from "../../components/CustomComponents/toast/toast.js";
import APIs from "../../Constants/apis.js";
import swal from "sweetalert";
import useThemeColor from "../../hooks/useThemeColor.js";
import useDarkMode from "../../hooks/useDarkMode.js";
import { logout } from "../../app/auth/authSlice.js";

const ManagePackages = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const themeColor = useThemeColor();
  const isDarkMode = useDarkMode();

  const [packages, setPackages] = useState([]);
  const [loading, setLoading] = useState(false);

  const fetchPackages = useCallback(async () => {
    try {
      setLoading(true);
      const response = await axiosInstance.get(APIs.GET_ALL_PACKAGES);

      if (response.data.status) {
        const packageData = response.data.data;
        if (Array.isArray(packageData)) {
          setPackages(packageData);
        } else {
          setPackages([]);
        }
      } else {
        showErrorToast(response.data.message || "Failed to fetch packages");
        setPackages([]);
      }
      setLoading(false);
    } catch (err) {
      console.log(err);
      setLoading(false);
      if (err.response && err.response.status === 401) {
        dispatch(logout());
        setTimeout(() => {
          navigate("/user/login", { replace: true });
        }, 1000);
      } else {
        showErrorToast("Failed to fetch packages");
      }
    }
  }, [dispatch, navigate]);

  useEffect(() => {
    fetchPackages();
  }, [fetchPackages]);

  const handleAddPackage = () => {
    navigate("/packages/addPackage");
  };

  const handleEditPackage = (pkg) => {
    navigate(`/packages/updatePackage/${pkg.id}`);
  };

  const handleDeletePackage = async (packageId) => {
    try {
      const result = await swal({
        title: "Are you sure?",
        text: "This will deactivate the package. Companies with active subscriptions will not be affected.",
        icon: "warning",
        buttons: true,
        dangerMode: true,
      });

      if (result) {
        setLoading(true);
        const response = await axiosInstance.delete(APIs.DELETE_PACKAGE, {
          params: { packageID: packageId },
        });

        if (response.data.status) {
          showSuccessToast("Package deleted successfully");
          fetchPackages();
        } else {
          showErrorToast(response.data.message || "Failed to delete package");
        }
        setLoading(false);
      }
    } catch (err) {
      console.log(err);
      setLoading(false);
      if (err.response && err.response.status === 401) {
        dispatch(logout());
        setTimeout(() => {
          navigate("/user/login", { replace: true });
        }, 1000);
      } else {
        showErrorToast("Failed to delete package");
      }
    }
  };

  const handleToggleStatus = async (packageId, currentStatus) => {
    try {
      setLoading(true);
      const response = await axiosInstance.patch(
        APIs.PATCH_UPDATE_PACKAGE_STATUS,
        null,
        {
          params: {
            packageID: packageId,
            status: !currentStatus,
          },
        }
      );

      if (response.data.status) {
        showSuccessToast("Package status updated successfully");
        fetchPackages();
      } else {
        showErrorToast(response.data.message || "Failed to update status");
      }
      setLoading(false);
    } catch (err) {
      console.log(err);
      setLoading(false);
      showErrorToast("Failed to update package status");
    }
  };

  const getPackageTypeLabel = (type) => {
    const types = {
      Starter: { label: "Starter", color: "#17a2b8" },
      Professional: { label: "Professional", color: "#28a745" },
      Enterprise: { label: "Enterprise", color: "#6f42c1" },
      Custom: { label: "Custom", color: "#fd7e14" },
    };
    return types[type] || { label: type, color: "#6c757d" };
  };

  return (
    <div className="content">
      {loading && <Preloader />}
      <div className="container-fluid">
        <div className="row">
          <div className="col-12">
            <div className="d-flex justify-content-between align-items-center mb-3">
              <div>
                <ol className="breadcrumb m-0">
                  <li className="breadcrumb-item">
                    <Link to="/dashboard">Dashboard</Link>
                  </li>
                  <li className="breadcrumb-item active">Manage Packages</li>
                </ol>
                <h1 className="page-header mb-0">Subscription Packages</h1>
              </div>
              <div className="ms-auto">
                <button
                  className="btn px-4"
                  onClick={handleAddPackage}
                  style={{
                    backgroundColor: themeColor,
                    background: `linear-gradient(135deg, ${themeColor} 0%, ${themeColor}dd 100%)`,
                    border: "none",
                    color: "white",
                    borderRadius: "25px",
                    fontWeight: "500",
                  }}
                >
                  <i className="fa fa-plus fa-lg me-2 ms-n2"></i>
                  Add Package
                </button>
              </div>
            </div>
          </div>
        </div>

        <div className="row">
          <div className="col-12">
            <div className="card">
              <div className="card-body">
                <div className="table-responsive">
                  <table className="table table-hover">
                    <thead
                      style={{
                        position: "sticky",
                        top: 0,
                        zIndex: 5,
                        backgroundColor: isDarkMode ? "#2d3436" : "#f8f9fa",
                        color: isDarkMode ? "#ffffff" : "#000000",
                      }}
                    >
                      <tr>
                        <th className="pt-2 pb-2" style={{ backgroundColor: isDarkMode ? "#2d3436" : "#f8f9fa", color: isDarkMode ? "#ffffff" : "#000000", borderBottom: isDarkMode ? "2px solid #404040" : "2px solid #dee2e6" }}>Package Name</th>
                        <th className="pt-2 pb-2" style={{ backgroundColor: isDarkMode ? "#2d3436" : "#f8f9fa", color: isDarkMode ? "#ffffff" : "#000000", borderBottom: isDarkMode ? "2px solid #404040" : "2px solid #dee2e6" }}>Trial Days</th>
                        <th className="pt-2 pb-2" style={{ backgroundColor: isDarkMode ? "#2d3436" : "#f8f9fa", color: isDarkMode ? "#ffffff" : "#000000", borderBottom: isDarkMode ? "2px solid #404040" : "2px solid #dee2e6" }}>Duration</th>
                        <th className="pt-2 pb-2" style={{ backgroundColor: isDarkMode ? "#2d3436" : "#f8f9fa", color: isDarkMode ? "#ffffff" : "#000000", borderBottom: isDarkMode ? "2px solid #404040" : "2px solid #dee2e6" }}>Devices</th>
                        <th className="pt-2 pb-2" style={{ backgroundColor: isDarkMode ? "#2d3436" : "#f8f9fa", color: isDarkMode ? "#ffffff" : "#000000", borderBottom: isDarkMode ? "2px solid #404040" : "2px solid #dee2e6" }}>Users</th>
                        <th className="pt-2 pb-2 text-center" style={{ backgroundColor: isDarkMode ? "#2d3436" : "#f8f9fa", color: isDarkMode ? "#ffffff" : "#000000", borderBottom: isDarkMode ? "2px solid #404040" : "2px solid #dee2e6" }}>Status</th>
                        <th className="pt-2 pb-2 text-center" style={{ backgroundColor: isDarkMode ? "#2d3436" : "#f8f9fa", color: isDarkMode ? "#ffffff" : "#000000", borderBottom: isDarkMode ? "2px solid #404040" : "2px solid #dee2e6" }}>Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {loading ? (
                        <tr>
                          <td colSpan="7" className="text-center">
                            <Preloader />
                          </td>
                        </tr>
                      ) : packages.length === 0 ? (
                        <tr>
                          <td colSpan="7" className="text-center">
                            No packages found
                          </td>
                        </tr>
                      ) : (
                        packages.map((pkg) => {
                          return (
                            <tr key={pkg.id}>
                              <td>
                                <strong>{pkg.pName}</strong>
                                {pkg.pDetail && (
                                  <div className="small" style={{ color: isDarkMode ? "#adb5bd" : "#6c757d" }}>
                                    {pkg.pDetail}
                                  </div>
                                )}
                              </td>
                              <td>{pkg.pTrialDays} days</td>
                              <td>{pkg.pDuration} days</td>
                              <td>{pkg.pAllowedDevices}</td>
                              <td>{pkg.pAllowedUsers}</td>
                              <td className="text-center">
                                <div className="form-check form-switch d-inline-block">
                                  <input
                                    className="form-check-input"
                                    type="checkbox"
                                    checked={pkg.pStatus}
                                    onChange={() =>
                                      handleToggleStatus(pkg.id, pkg.pStatus)
                                    }
                                    style={{ cursor: "pointer" }}
                                  />
                                </div>
                              </td>
                              <td className="text-center">
                                <div className="d-flex justify-content-center gap-2">
                                  {/* Edit Button */}
                                  <button
                                    className="btn btn-sm btn-outline-primary"
                                    style={{
                                      width: '32px',
                                      height: '32px',
                                      display: 'inline-flex',
                                      alignItems: 'center',
                                      justifyContent: 'center',
                                      borderRadius: '6px',
                                      border: `1px solid ${themeColor}`,
                                      color: themeColor,
                                      transition: 'all 0.2s ease',
                                      padding: 0
                                    }}
                                    onClick={() => handleEditPackage(pkg)}
                                    title="Edit Package"
                                    onMouseEnter={(e) => {
                                      e.currentTarget.style.backgroundColor = themeColor;
                                      e.currentTarget.style.color = 'white';
                                      e.currentTarget.style.transform = 'scale(1.05)';
                                    }}
                                    onMouseLeave={(e) => {
                                      e.currentTarget.style.backgroundColor = 'transparent';
                                      e.currentTarget.style.color = themeColor;
                                      e.currentTarget.style.transform = 'scale(1)';
                                    }}
                                  >
                                    <i className="fa fa-edit" style={{ fontSize: '12px' }}></i>
                                  </button>

                                  {/* Delete Button */}
                                  <button
                                    className="btn btn-sm btn-outline-danger"
                                    style={{
                                      width: '32px',
                                      height: '32px',
                                      display: 'inline-flex',
                                      alignItems: 'center',
                                      justifyContent: 'center',
                                      borderRadius: '6px',
                                      border: '1px solid #dc3545',
                                      color: '#dc3545',
                                      transition: 'all 0.2s ease',
                                      padding: 0
                                    }}
                                    onClick={() => handleDeletePackage(pkg.id)}
                                    title="Delete Package"
                                    onMouseEnter={(e) => {
                                      e.currentTarget.style.backgroundColor = '#dc3545';
                                      e.currentTarget.style.color = 'white';
                                      e.currentTarget.style.transform = 'scale(1.05)';
                                    }}
                                    onMouseLeave={(e) => {
                                      e.currentTarget.style.backgroundColor = 'transparent';
                                      e.currentTarget.style.color = '#dc3545';
                                      e.currentTarget.style.transform = 'scale(1)';
                                    }}
                                  >
                                    <i className="fa fa-trash" style={{ fontSize: '12px' }}></i>
                                  </button>
                                </div>
                              </td>
                            </tr>
                          );
                        })
                      )}
                    </tbody>
                  </table>
                </div>

                <div className="row mt-3">
                  <div className="col-12">
                    <p style={{ color: isDarkMode ? "#adb5bd" : "#6c757d" }}>
                      Showing {packages.length} package(s)
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ManagePackages;

